import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { diaryService } from '../../services/diary';

const TagSelector = ({ selectedTags = [], onChange, className = '' }) => {
  const [availableTags, setAvailableTags] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const tags = await diaryService.getTags();
        setAvailableTags(tags);
      } catch (error) {
        console.error('Error fetching tags:', error);
      }
    };
    fetchTags();
  }, []);

  const filteredTags = availableTags.filter(
    tag => 
      tag.toLowerCase().includes(inputValue.toLowerCase()) &&
      !selectedTags.includes(tag)
  );

  const addTag = (tag) => {
    if (tag && !selectedTags.includes(tag)) {
      onChange([...selectedTags, tag]);
      setInputValue('');
      setShowSuggestions(false);
    }
  };

  const removeTag = (tagToRemove) => {
    onChange(selectedTags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      addTag(inputValue.trim().toLowerCase());
    } else if (e.key === 'Backspace' && !inputValue && selectedTags.length > 0) {
      removeTag(selectedTags[selectedTags.length - 1]);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Tags
      </label>
      
      {/* Selected Tags */}
      <div className="flex flex-wrap gap-2 mb-3">
        <AnimatePresence>
          {selectedTags.map((tag) => (
            <motion.span
              key={tag}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-700 border border-primary-200"
            >
              #{tag}
              <motion.button
                type="button"
                onClick={() => removeTag(tag)}
                className="ml-2 hover:text-primary-900 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <XMarkIcon className="h-3 w-3" />
              </motion.button>
            </motion.span>
          ))}
        </AnimatePresence>
      </div>

      {/* Input */}
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setShowSuggestions(true);
          }}
          onKeyDown={handleKeyPress}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          className="input-field"
          placeholder="Add tags (press Enter to add)"
        />
        
        {/* Suggestions Dropdown */}
        <AnimatePresence>
          {showSuggestions && (inputValue || filteredTags.length > 0) && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto"
            >
              {inputValue && !availableTags.includes(inputValue.toLowerCase()) && (
                <motion.button
                  type="button"
                  onClick={() => addTag(inputValue.trim().toLowerCase())}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-2 border-b border-gray-100"
                  whileHover={{ backgroundColor: '#f9fafb' }}
                >
                  <PlusIcon className="h-4 w-4 text-green-500" />
                  <span>Create "{inputValue.toLowerCase()}"</span>
                </motion.button>
              )}
              
              {filteredTags.map((tag) => (
                <motion.button
                  key={tag}
                  type="button"
                  onClick={() => addTag(tag)}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors"
                  whileHover={{ backgroundColor: '#f9fafb' }}
                >
                  #{tag}
                </motion.button>
              ))}
              
              {filteredTags.length === 0 && inputValue && availableTags.includes(inputValue.toLowerCase()) && (
                <div className="px-4 py-2 text-gray-500 text-sm">
                  Tag already selected
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      <p className="mt-1 text-xs text-gray-500">
        Press Enter to add tags. Click on tags to remove them.
      </p>
    </div>
  );
};

export default TagSelector;
